"use client";

import { createCorrelationId, normalizeCorrelationId } from "@/src/observability/correlation";
import { isAllowedClientOperationalEvent } from "@/src/observability/clientContract";
import { toRoutePattern } from "@/src/observability/routePattern";
import { isInternalTelemetryPath } from "@/src/observability/transport";
import { getSession, hasBrowserSession } from "@/src/shared/browser/sessionStorage";
import { isBenignWindowError } from "@/src/shared/browser/benignWindowErrors";

const STORAGE_KEY = "smartmanager.operational-events.v1";
const MAX_EVENTS = 200;
const MAX_BATCH = 25;
const HEARTBEAT_MS = 5 * 60_000;
const FLUSH_RETRY_BASE_MS = 5_000;
const FLUSH_RETRY_MAX_MS = 60_000;
// A fetch that rejects shortly after the page began unloading was torn down by
// the navigation, not by the network.
const UNLOAD_CANCEL_WINDOW_MS = 5_000;
const BUFFER_DROPPED_EVENT = "observability.buffer.dropped";
const SAFE_LABEL = /^[a-zA-Z0-9._:/ -]{1,160}$/;

export type ClientOperationalEventInput = Readonly<{
  event_name: string;
  severity: "debug" | "info" | "warn" | "error";
  correlation_id?: string | null;
  route?: string | null;
  method?: string | null;
  status?: number | null;
  duration_ms?: number | null;
  count?: number | null;
  error_type?: string | null;
  source?: string | null;
}>;

type BufferedClientEvent = ClientOperationalEventInput & Readonly<{
  timestamp: string;
  runtime: "browser" | "electron_renderer";
}>;

type ElectronObservabilityBridge = {
  isElectron?: boolean;
  reportOperationalEvents?: (events: BufferedClientEvent[], context: { sessionId: unknown }) => Promise<unknown>;
  setObservabilityConsent?: (granted: boolean) => Promise<unknown>;
};

/**
 * The desktop main process buffers and sends its own events (crashes, the
 * updater) and cannot read this consent, so every decision is forwarded.
 */
function forwardConsentToDesktop(granted: boolean): void {
  const forward = electronBridge()?.setObservabilityConsent;
  if (typeof forward !== "function") return;
  try {
    void Promise.resolve(forward(granted)).catch(() => undefined);
  } catch {
    // An older desktop build without the channel: main stays closed.
  }
}

let initialized = false;
// Diagnostics are opt-in (tracker consent "diagnostics" category). Until
// `initializeClientObservability` runs, nothing is recorded, buffered or sent.
let diagnosticsEnabled = false;
let memoryQueue: BufferedClientEvent[] = [];
// Entries handed to the transport. They are never merged into or evicted, and a
// delivered batch is removed by identity so concurrent enqueues cannot shift it.
const inFlight = new Set<BufferedClientEvent>();
let flushing = false;
let flushRetryDelayMs = 0;
let flushBlockedUntil = 0;
let flushRetryTimer: ReturnType<typeof setTimeout> | null = null;
let pageUnloadStartedAt: number | null = null;
let activeCorrelation: { id: string; expiresAt: number } | null = null;
let nativeFetch: typeof window.fetch | null = null;

function electronBridge(): ElectronObservabilityBridge | undefined {
  return (window as Window & { electronAPI?: ElectronObservabilityBridge }).electronAPI;
}

export function activateClientCorrelation(value?: unknown): string {
  const id = normalizeCorrelationId(value) ?? createCorrelationId();
  activeCorrelation = { id, expiresAt: Date.now() + 5_000 };
  return id;
}

export function getActiveClientCorrelationId(): string {
  if (activeCorrelation && activeCorrelation.expiresAt >= Date.now()) return activeCorrelation.id;
  return activateClientCorrelation();
}

function safeLabel(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return SAFE_LABEL.test(normalized) ? normalized : undefined;
}

/** The app route pattern, never the real path: dynamic segments can be capabilities or names. */
function safeRoute(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    return toRoutePattern(new URL(value, window.location.origin).pathname);
  } catch {
    return undefined;
  }
}

function sanitize(input: ClientOperationalEventInput): BufferedClientEvent | null {
  const eventName = safeLabel(input.event_name);
  if (!eventName || !["debug", "info", "warn", "error"].includes(input.severity)) return null;
  const runtime = electronBridge()?.isElectron ? "electron_renderer" : "browser";
  const source = safeLabel(input.source);
  if (!isAllowedClientOperationalEvent({ runtime, eventName, source })) return null;
  const status = Number(input.status);
  const durationMs = Number(input.duration_ms);
  const count = Number(input.count);
  return {
    event_name: eventName,
    severity: input.severity,
    correlation_id: normalizeCorrelationId(input.correlation_id),
    route: safeRoute(input.route),
    method: safeLabel(input.method),
    status: Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined,
    duration_ms: Number.isFinite(durationMs) && durationMs >= 0 ? Math.round(durationMs) : undefined,
    count: Number.isFinite(count) && count >= 0 ? Math.round(count) : undefined,
    error_type: safeLabel(input.error_type),
    source,
    timestamp: new Date().toISOString(),
    runtime,
  };
}

function readStored(): BufferedClientEvent[] {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value.slice(-MAX_EVENTS) : [];
  } catch {
    return [];
  }
}

/** How many original events an entry stands for (drop markers carry their tally). */
function eventWeight(event: BufferedClientEvent): number {
  if (event.event_name === BUFFER_DROPPED_EVENT) return event.count ?? 0;
  return event.count ?? 1;
}

/**
 * Repeated warnings/errors collapse into one queued entry with a count, so an
 * outage that fails the same request on every poll ships one aggregated event
 * instead of one per attempt. Drop markers collapse per source the same way.
 */
function coalesceKey(event: BufferedClientEvent): string | null {
  if (event.event_name === BUFFER_DROPPED_EVENT) return `${BUFFER_DROPPED_EVENT}|${event.source ?? ""}`;
  if (event.severity !== "warn" && event.severity !== "error") return null;
  return [
    event.event_name,
    event.severity,
    event.runtime,
    event.route ?? "",
    event.method ?? "",
    event.status ?? "",
    event.error_type ?? "",
    event.source ?? "",
  ].join("|");
}

function mergeIntoQueued(event: BufferedClientEvent): boolean {
  const key = coalesceKey(event);
  if (!key) return false;
  const index = memoryQueue.findIndex((queued) => !inFlight.has(queued) && coalesceKey(queued) === key);
  if (index < 0) return false;
  const existing = memoryQueue[index];
  memoryQueue[index] = {
    ...existing,
    count: eventWeight(existing) + eventWeight(event),
    duration_ms: event.duration_ms ?? existing.duration_ms,
  };
  return true;
}

/** Evict the oldest routine event first so buffered warnings and errors survive an outage. */
function evictOne(): number {
  let fallback = -1;
  for (let index = 0; index < memoryQueue.length; index += 1) {
    const event = memoryQueue[index];
    if (inFlight.has(event) || event.event_name === BUFFER_DROPPED_EVENT) continue;
    if (event.severity === "debug" || event.severity === "info") {
      memoryQueue.splice(index, 1);
      return eventWeight(event);
    }
    if (fallback < 0) fallback = index;
  }
  if (fallback < 0) return 0;
  const [event] = memoryQueue.splice(fallback, 1);
  return eventWeight(event);
}

function enqueueDropMarker(count: number, source: string): void {
  if (count <= 0) return;
  const marker = sanitize({
    event_name: BUFFER_DROPPED_EVENT,
    severity: "warn",
    correlation_id: getActiveClientCorrelationId(),
    count,
    source,
  });
  if (!marker || mergeIntoQueued(marker)) return;
  let total = count;
  while (memoryQueue.length >= MAX_EVENTS) {
    const evicted = evictOne();
    if (evicted === 0) break;
    total += evicted;
  }
  memoryQueue.push({ ...marker, count: total });
}

function enqueue(event: BufferedClientEvent): void {
  if (mergeIntoQueued(event)) return;
  memoryQueue.push(event);
  let dropped = 0;
  while (memoryQueue.length > MAX_EVENTS) {
    const evicted = evictOne();
    if (evicted === 0) break;
    dropped += evicted;
  }
  enqueueDropMarker(dropped, "browser-buffer");
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryQueue.slice(-MAX_EVENTS)));
  } catch {
    const keep = Math.floor(MAX_EVENTS / 2);
    let dropped = 0;
    while (memoryQueue.length > keep) {
      const evicted = evictOne();
      if (evicted === 0) break;
      dropped += evicted;
    }
    enqueueDropMarker(dropped, "browser-storage");
  }
}

async function sendBatch(events: BufferedClientEvent[]): Promise<boolean> {
  const electronReporter = electronBridge()?.reportOperationalEvents;
  if (typeof electronReporter === "function") {
    try {
      return (await electronReporter(events, { sessionId: getSession() })) === true;
    } catch {
      return false;
    }
  }
  try {
    const correlationId = events.map((event) => event.correlation_id).find(Boolean) || createCorrelationId();
    const response = await (nativeFetch ?? window.fetch)("/api/observability/client", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-correlation-id": correlationId },
      credentials: "same-origin",
      body: JSON.stringify({ events }),
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
}

function scheduleFlushRetry(): void {
  flushRetryDelayMs = Math.min(
    FLUSH_RETRY_MAX_MS,
    flushRetryDelayMs > 0 ? flushRetryDelayMs * 2 : FLUSH_RETRY_BASE_MS,
  );
  flushBlockedUntil = Date.now() + flushRetryDelayMs;
  // The retry must fire when the (possibly longer) new block ends; a timer left
  // from the previous backoff would wake early and find the flush still blocked.
  if (flushRetryTimer !== null) clearTimeout(flushRetryTimer);
  flushRetryTimer = setTimeout(() => {
    flushRetryTimer = null;
    void flushClientObservability();
  }, flushRetryDelayMs);
}

export async function flushClientObservability(): Promise<void> {
  if (
    flushing
    || memoryQueue.length === 0
    || !hasBrowserSession()
    || ("onLine" in navigator && !navigator.onLine)
    // A failed delivery (server down, session not yet valid) backs off instead
    // of re-posting on every recorded event.
    || Date.now() < flushBlockedUntil
  ) return;
  flushing = true;
  try {
    while (memoryQueue.length > 0) {
      const batch = memoryQueue.slice(0, MAX_BATCH);
      for (const event of batch) inFlight.add(event);
      let delivered = false;
      try {
        delivered = await sendBatch(batch);
      } finally {
        for (const event of batch) inFlight.delete(event);
      }
      if (!delivered) {
        scheduleFlushRetry();
        break;
      }
      flushRetryDelayMs = 0;
      flushBlockedUntil = 0;
      const sent = new Set(batch);
      memoryQueue = memoryQueue.filter((event) => !sent.has(event));
      persist();
    }
  } finally {
    flushing = false;
  }
}

export function recordClientOperationalEvent(input: ClientOperationalEventInput): string {
  const correlationId = normalizeCorrelationId(input.correlation_id) ?? getActiveClientCorrelationId();
  if (!diagnosticsEnabled) return correlationId;
  if (typeof window === "undefined" || !hasBrowserSession()) return correlationId;
  const event = sanitize({ ...input, correlation_id: correlationId });
  if (!event) return correlationId;
  enqueue(event);
  persist();
  void flushClientObservability();
  return correlationId;
}

function isTrustedApiUrl(url: URL): boolean {
  if (!url.pathname.startsWith("/api/") || isInternalTelemetryPath(url.pathname)) return false;
  if (url.origin === window.location.origin) return true;
  return url.protocol === "https:" && (url.hostname === "smartmanager.ai" || url.hostname.endsWith(".smartmanager.ai"));
}

function errorName(error: unknown): string | undefined {
  return safeLabel((error as { name?: unknown } | null)?.name);
}

/**
 * Aborted requests and requests torn down by a page navigation (browsers reject
 * those with a generic TypeError) are expected lifecycle, not failures.
 * Timeouts stay failures even though they abort the signal.
 */
function isCancelledRequest(error: unknown, input: RequestInfo | URL, init?: RequestInit): boolean {
  const name = errorName(error);
  if (name === "TimeoutError") return false;
  if (name === "AbortError") return true;
  if (init?.signal?.aborted || (input instanceof Request && input.signal.aborted)) return true;
  // Heuristic: browsers give no signal that a TypeError came from a navigation
  // teardown, so any network TypeError within 5s of beforeunload/pagehide counts
  // as cancelled. A genuine outage in that window is misfiled as cancelled (info),
  // and an unload that is itself cancelled (no pageshow follows) keeps the window
  // open for its 5s. Failures outside the window are unaffected.
  return pageUnloadStartedAt !== null && performance.now() - pageUnloadStartedAt <= UNLOAD_CANCEL_WINDOW_MS;
}

function installFetchInstrumentation(): void {
  if (nativeFetch) return;
  nativeFetch = window.fetch.bind(window);
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    let url: URL;
    try {
      url = new URL(input instanceof Request ? input.url : String(input), window.location.href);
    } catch {
      return nativeFetch!(input, init);
    }
    if (!isTrustedApiUrl(url)) return nativeFetch!(input, init);

    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
    const correlationId = normalizeCorrelationId(headers.get("x-correlation-id")) ?? getActiveClientCorrelationId();
    headers.set("x-correlation-id", correlationId);
    const method = (init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase();
    const startedAt = performance.now();
    recordClientOperationalEvent({
      event_name: "browser.api_request.started",
      severity: "info",
      correlation_id: correlationId,
      route: url.pathname,
      method,
    });
    try {
      const response = await nativeFetch!(input, { ...init, headers });
      recordClientOperationalEvent({
        event_name: response.ok ? "browser.api_request.completed" : "browser.api_request.failed",
        severity: response.status >= 500 ? "error" : response.ok ? "info" : "warn",
        correlation_id: correlationId,
        route: url.pathname,
        method,
        status: response.status,
        duration_ms: performance.now() - startedAt,
        error_type: response.ok ? undefined : "HttpError",
      });
      return response;
    } catch (error) {
      const cancelled = isCancelledRequest(error, input, init);
      recordClientOperationalEvent({
        event_name: cancelled ? "browser.api_request.cancelled" : "browser.api_request.failed",
        severity: cancelled ? "info" : "error",
        correlation_id: correlationId,
        route: url.pathname,
        method,
        duration_ms: performance.now() - startedAt,
        error_type: errorName(error) || "NetworkError",
      });
      throw error;
    }
  }) as typeof window.fetch;
}

/**
 * Stops diagnostics after consent is withdrawn: recording becomes a no-op and
 * the buffered events on this device are discarded, never sent.
 */
export function disableClientObservability(): void {
  diagnosticsEnabled = false;
  if (typeof window !== "undefined") forwardConsentToDesktop(false);
  memoryQueue = [];
  if (flushRetryTimer) {
    clearTimeout(flushRetryTimer);
    flushRetryTimer = null;
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing was persisted.
  }
}

/** Starts diagnostics. Call only when tracker consent allows the "diagnostics" category. */
export function initializeClientObservability(): void {
  if (typeof window === "undefined") return;
  // Before any record: main refuses renderer events until it has consent.
  forwardConsentToDesktop(true);
  if (initialized) {
    diagnosticsEnabled = true;
    return;
  }
  initialized = true;
  diagnosticsEnabled = true;
  // Re-enqueue so a buffer persisted by an older build (or a long outage)
  // collapses its repeated failures and drop markers before delivery.
  memoryQueue = [];
  for (const event of readStored()) enqueue(event);
  installFetchInstrumentation();
  recordClientOperationalEvent({
    event_name: "observability.source.heartbeat",
    severity: "info",
    source: electronBridge()?.isElectron ? "electron-renderer-startup" : "browser-startup",
  });
  window.addEventListener("online", () => {
    flushBlockedUntil = 0;
    void flushClientObservability();
  });
  const markPageUnloading = () => {
    pageUnloadStartedAt = performance.now();
  };
  window.addEventListener("beforeunload", markPageUnloading);
  window.addEventListener("pagehide", markPageUnloading);
  window.addEventListener("pageshow", () => {
    pageUnloadStartedAt = null;
  });
  window.addEventListener("error", (event) => {
    if (isBenignWindowError(event)) return;
    recordClientOperationalEvent({
      event_name: "browser.runtime.error",
      severity: "error",
      error_type: safeLabel(event.error?.name) || "Error",
      source: "window-error",
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordClientOperationalEvent({
      event_name: "browser.promise.unhandled",
      severity: "error",
      error_type: safeLabel(event.reason?.name) || "UnhandledRejection",
      source: "unhandled-rejection",
    });
  });
  const interval = window.setInterval(() => {
    recordClientOperationalEvent({
      event_name: "observability.source.heartbeat",
      severity: "info",
      source: electronBridge()?.isElectron ? "electron-renderer" : "browser",
    });
  }, HEARTBEAT_MS);
  window.addEventListener("pagehide", () => {
    window.clearInterval(interval);
    void flushClientObservability();
  }, { once: true });
}
